import React from "react";
import { Link } from "react-router-dom";
import { FOOTER_MASTER_LOGO } from "../TritalLogo";
import {
  Linkedin, Twitter, Facebook, Instagram, Mail, Phone, MapPin, ExternalLink,
} from "lucide-react";

const PHONE_DISPLAY = "+1 (832) 303-6622";
const PHONE_DIAL    = "+18323036622";
const EMAIL         = "info@tritalcare.com";
const SHIFTS_URL    = "https://shifts.tritalcare.com/";

const OFFICES = [
  { label: "USA — Head Office",  lines: ["5423 Pecan Fall PL", "Missouri City, TX 77459"] },
  { label: "USA — Corporate",    lines: ["11104 West Airport Blvd, Suite 213", "Stafford, TX 77477"] },
  { label: "India — Operations", lines: ["First Floor, A-83, A Block", "Sector 63, Noida, UP 201309"] },
];

const COLUMNS = [
  {
    heading: "Employers",
    links: [
      { label: "For Employers",      to: "/employers" },
      { label: "Contract Staffing",  to: "/employers#contract-staffing" },
      { label: "Permanent Staffing", to: "/employers#permanent-staffing" },
      { label: "Payroll Services",   to: "/employers#payroll" },
      { label: "EOR / AOR",          to: "/employers#eor-aor" },
      { label: "Workforce Solutions", to: "/employers" },
    ],
  },
  {
    heading: "Clinicians",
    links: [
      { label: "For Clinicians",  to: "/clinicians" },
      { label: "PRN Shifts",      to: SHIFTS_URL, external: true },
      { label: "Travel Nursing",  to: "/services/travel-nursing" },
      { label: "Local & Per Diem",to: "/services/local-per-diem-staffing" },
      { label: "Checklists",      to: "/compliance/checklists" },
    ],
  },
  {
    heading: "Services",
    links: [
      { label: "All Services",           to: "/services" },
      { label: "Allied Health",          to: "/services/allied-health-staffing" },
      { label: "Therapy",                 to: "/services/therapy-staffing" },
      { label: "Physician & APP",        to: "/services/physician-app-staffing" },
      { label: "Executive Leadership",   to: "/services/executive-leadership" },
    ],
  },
  {
    heading: "Platforms",
    links: [
      { label: "All Platforms",      to: "/platforms" },
      { label: "ShiftBridge PRN",    to: SHIFTS_URL, external: true },
      { label: "Workforce OS Tools", to: "/workforce-os" },
      { label: "RateLab V9",         to: "/workforce-os/ratelab" },
      { label: "Resume Formatter",   to: "/workforce-os/resume-formatter" },
      { label: "Submittal Score",    to: "/workforce-os/submittal-score" },
      { label: "MSP Readiness",      to: "/workforce-os/msp-readiness" },
    ],
  },
  {
    heading: "Resources",
    links: [
      { label: "Compliance",            to: "/compliance" },
      { label: "Diversity & Inclusion", to: "/diversity-and-inclusion" },
      { label: "Blog / Resources",      to: "/blog" },
      { label: "Capability Statement",  to: "/capability-statement" },
      { label: "Verify Statement",      to: "/verify" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About",   to: "/about" },
      { label: "Careers", to: "/careers" },
      { label: "Platforms", to: "/platforms" },
      { label: "Find Jobs", to: "/careers/jobs" },
      { label: "Hot Jobs", to: "/hot-jobs" },
      { label: "Contact", to: "/contact" },
    ],
  },
];

function FooterLink({ l }) {
  const cls = "text-[13.5px] text-slate-300 hover:text-white transition-colors leading-relaxed";
  const testid = `footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  if (l.external) {
    return (
      <a href={l.to} target="_blank" rel="noopener noreferrer" className={`${cls} inline-flex items-center gap-1`} data-testid={testid}>
        {l.label} <ExternalLink className="w-3 h-3 opacity-60" strokeWidth={1.8} />
      </a>
    );
  }
  if (l.to.includes("#")) {
    return <a href={l.to} className={cls} data-testid={testid}>{l.label}</a>;
  }
  return <Link to={l.to} className={cls} data-testid={testid}>{l.label}</Link>;
}

export default function PublicFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-trital-navy-ink text-slate-200 mt-24" data-testid="public-footer" role="contentinfo">
      {/* ============ BRAND BAND ============ */}
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 pt-16 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] gap-10 lg:gap-14 items-start">
          {/* Brand column — locked master logo + description + socials */}
          <div>
            <Link to="/" className="inline-block -ml-3 lg:-ml-5" aria-label="TRITAL Care — home" data-testid="footer-logo-link">
              <img
                src={FOOTER_MASTER_LOGO}
                alt="TRITAL Care® — Triage Precision. Trusted Workforce."
                className="h-44 md:h-56 lg:h-60 w-auto select-none drop-shadow-[0_2px_14px_rgba(0,0,0,0.4)]"
                draggable={false}
                width={685}
                height={194}
              />
            </Link>
            <p className="mt-2 text-[13.5px] text-slate-300/85 leading-relaxed max-w-[52ch]">
              Clinical workforce infrastructure for hospitals, health systems, skilled nursing operators, MSPs and the professionals who staff them — orchestrated on a compliance-first platform.
            </p>
            <p className="mt-4 text-[12px] text-slate-400/85 leading-relaxed">
              TRITAL Care<sup className="text-[8px]">®</sup> is the operating brand name of <span className="text-white/85">TOPTAL Care Inc.</span>
            </p>
            <div className="mt-6 flex items-center gap-2.5">
              {[
                { Icon: Linkedin,  href: "https://www.linkedin.com", label: "LinkedIn", testid: "linkedin" },
                { Icon: Twitter,   href: "https://x.com",            label: "X (Twitter)", testid: "twitter" },
                { Icon: Facebook,  href: "https://www.facebook.com", label: "Facebook", testid: "facebook" },
                { Icon: Instagram, href: "https://www.instagram.com",label: "Instagram", testid: "instagram" },
              ].map(({ Icon, href, label, testid }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-slate-300 hover:bg-white hover:text-trital-navy hover:border-white transition-colors"
                  data-testid={`footer-social-${testid}`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.8} />
                </a>
              ))}
            </div>
          </div>

          {/* Contact / CTA card */}
          <div className="rounded-2xl bg-white/[0.035] border border-white/10 p-6 lg:p-7 backdrop-blur-sm">
            <div className="overline text-slate-400">Direct contact</div>
            <ul className="mt-3 space-y-2.5 text-[13.5px]">
              <li className="flex items-center gap-2.5">
                <Mail className="w-4 h-4 text-trital-red shrink-0" strokeWidth={1.8} />
                <a href={`mailto:${EMAIL}`} className="text-slate-200 hover:text-white transition-colors" data-testid="footer-email">{EMAIL}</a>
              </li>
              <li className="flex items-center gap-2.5 flex-wrap">
                <Phone className="w-4 h-4 text-trital-red shrink-0" strokeWidth={1.8} />
                <a href={`tel:${PHONE_DIAL}`} className="text-slate-200 hover:text-white transition-colors tabular-nums" data-testid="footer-phone">
                  +1 (832) 303-6622
                </a>
                <span className="text-white/40 text-[11px]">({PHONE_DISPLAY})</span>
              </li>
            </ul>

            <div className="h-px bg-white/10 my-5" />

            <div className="overline text-slate-400">Offices</div>
            <ul className="mt-3 space-y-3 text-[13px]" data-testid="footer-offices">
              {OFFICES.map((o) => (
                <li key={o.label} className="flex items-start gap-2.5">
                  <MapPin className="w-3.5 h-3.5 text-trital-red mt-0.5 shrink-0" strokeWidth={1.8} />
                  <div>
                    <div className="text-white/85 font-semibold">{o.label}</div>
                    <div className="mt-0.5 text-slate-400/90 leading-relaxed">
                      {o.lines.map((l, i) => <div key={i}>{l}</div>)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* ============ LINK GRID ============ */}
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-10">
          {COLUMNS.map((col) => (
            <nav
              key={col.heading}
              aria-label={col.heading}
              data-testid={`footer-col-${col.heading.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <div className="overline text-slate-400">{col.heading}</div>
              <ul className="mt-3.5 space-y-2.5">
                {col.links.map((l) => <li key={l.label}><FooterLink l={l} /></li>)}
              </ul>
            </nav>
          ))}
        </div>
      </div>

      {/* ============ LEGAL BAR ============ */}
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[11.5px] text-slate-400">
          <div className="leading-relaxed">
            © {year} TOPTAL Care Inc. · TRITAL Care<sup className="text-[8px]">®</sup> is the operating brand name of TOPTAL Care Inc. All rights reserved.
            <span className="hidden md:inline text-slate-600"> · </span>
            <span className="block md:inline mt-1 md:mt-0 text-slate-500">HIPAA-aware · SOC 2 aligned · ISO 27001-aligned controls</span>
          </div>
          <div className="flex gap-5 shrink-0">
            <Link to="/legal/privacy" className="hover:text-white transition-colors" data-testid="footer-legal-privacy">Privacy</Link>
            <Link to="/legal/terms" className="hover:text-white transition-colors" data-testid="footer-legal-terms">Terms</Link>
            <Link to="/legal/cookies" className="hover:text-white transition-colors" data-testid="footer-legal-cookies">Cookies</Link>
            <Link to="/legal/accessibility" className="hover:text-white transition-colors" data-testid="footer-legal-accessibility">Accessibility</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
